import { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedState } from './SharedStateContext';
import { getCurrentUserId, getUserMemory, getCurrentUserName, getUserWork, getFirestoreDocId } from '../utilities/firebase';
import { fetchExistingPlanner, buildReplanRequest, plannerSignature } from '../utilities/lessonPlanning';
import { getStoredWorkInfo } from '../utilities/workInfo';
import { trackPlannerCreated, trackLearningGoalCreated, trackLearningGoalEdited } from '../utilities/tracking';
import {
  subscribeActivityProgressUpdates,
  isLearningPlanComplete,
  syncActivityProgressCacheToPlan,
} from '../utilities/activitySelection';
import { _config } from '../Constants';
import axios from 'axios';
import toast from 'react-simple-toasts';
import * as Sentry from '@sentry/react';

const PlannerContext = createContext();

// Someone who just finished every activity is starting their next chapter, not
// throwing progress away — the generic "this will replace all your progress"
// warning reads as a punishment and stops them creating a new plan.
const getReplacePlanConfirmMessage = (plannerData, t) => (
  isLearningPlanComplete(plannerData)
    ? t(
        'confirmNewLearningPlanAfterCompletion',
        "You've finished this plan. Creating a new one replaces it with fresh activities. Continue?"
      )
    : t('confirmReplaceLearningPlan')
);

export function usePlanner() {
  const context = useContext(PlannerContext);
  // Return null if no provider (e.g., in lite mode)
  if (context === undefined) {
    return null;
  }
  return context;
}

export const PlannerProvider = ({ children }) => {
  const { t } = useTranslation();
  const { sharedState, updateSelectedActivity, getLatestLevel, switchToPlannerView, switchToConversationView, switchToActivitiesView, startConversation } = useSharedState();
  
  const [showPlannerModal, setShowPlannerModal] = useState(false);
  const [showPlannerList, setShowPlannerList] = useState(false);
  const [currentPlannerData, setCurrentPlannerData] = useState(null);
  const [plannerGoalForEdit, setPlannerGoalForEdit] = useState('');
  const [isPlannerLoading, setIsPlannerLoading] = useState(false);
  const [isPlannerPending, setIsPlannerPending] = useState(false);
  const [pendingPlannerObjective, setPendingPlannerObjective] = useState('');
  const [hasPlannerNotification, setHasPlannerNotification] = useState(false);
  const activePollRef = useRef(null); // Track active poll timeout to cancel on new submission

  const URL = _config.url;
  const URL_PLANNER = `${URL}planner`;

  useEffect(() => {
    const unsubscribe = subscribeActivityProgressUpdates(({ userId, activityId, progress }) => {
      if (userId && userId !== getCurrentUserId()) return;
      if (!activityId || !progress) return;

      setCurrentPlannerData(prevPlannerData => {
        if (!prevPlannerData?.[activityId]) {
          return prevPlannerData;
        }

        return {
          ...prevPlannerData,
          [activityId]: {
            ...prevPlannerData[activityId],
            progress: {
              ...(prevPlannerData[activityId].progress || {}),
              ...progress,
            },
          },
        };
      });
    });

    return unsubscribe;
  }, []);

  // Cached progress is keyed by activity position, so it only means something
  // for the plan it was recorded against. Every path that swaps the plan
  // (creation, polling, the objective page) lands here, so this is the one
  // place that has to notice.
  useEffect(() => {
    syncActivityProgressCacheToPlan(currentPlannerData);
  }, [currentPlannerData]);

  // Helper to clear pending state
  const clearPendingState = () => {
    setIsPlannerPending(false);
    setPendingPlannerObjective('');
    localStorage.removeItem('plannerPending');
    localStorage.removeItem('plannerPendingObjective');
    localStorage.removeItem('plannerPendingStartTime');
    localStorage.removeItem('plannerPendingPreviousSignature');
  };

  // Check for existing planner and handle pending planner from onboarding
  useEffect(() => {
    let pollInterval = null;
    let pendingTimeout = null;
    const PENDING_TIMEOUT_MS = 3 * 60 * 1000; // 3 minute timeout

    const checkPlannerNotification = async () => {
      setIsPlannerLoading(true);

      // Check if there's a pending planner from onboarding
      const plannerPending = localStorage.getItem('plannerPending') === 'true';
      const pendingObjective = localStorage.getItem('plannerPendingObjective') || '';
      const pendingStartTime = localStorage.getItem('plannerPendingStartTime');

      // Check if pending state has expired (over 1 hour old)
      if (plannerPending && pendingStartTime) {
        const elapsedTime = Date.now() - parseInt(pendingStartTime, 10);
        if (elapsedTime > PENDING_TIMEOUT_MS) {
          console.log('🗺️ Pending planner timed out after 3 minutes, clearing state');
          clearPendingState();
          setIsPlannerLoading(false);
          return;
        }
      }

      try {
        // When a regeneration is pending for a user who already has a plan,
        // the latest planner may still be the OLD one — only a signature
        // change proves the new plan has landed.
        const previousSignature = localStorage.getItem('plannerPendingPreviousSignature') || '';
        const existingPlanner = await fetchExistingPlanner();
        const hasPlan = existingPlanner && existingPlanner.number_activities > 0;
        const isStalePlan = plannerPending && previousSignature
          && hasPlan && plannerSignature(existingPlanner) === previousSignature;

        if (hasPlan && !isStalePlan) {
          // Planner exists and is complete
          setCurrentPlannerData(existingPlanner);

          // Clear pending flags if the planner matches our pending objective
          if (plannerPending) {
            clearPendingState();
            console.log('🗺️ Pending planner is now ready!');
          }

          // Check if user has viewed this planner before
          const viewedPlanners = JSON.parse(localStorage.getItem('viewedPlanners') || '[]');
          const plannerKey = `${getCurrentUserId()}_${existingPlanner.goal}`;
          if (!viewedPlanners.includes(plannerKey)) {
            setHasPlannerNotification(true);
          }
        } else if (plannerPending) {
          if (isStalePlan) {
            // Keep showing the old plan while the replacement generates
            setCurrentPlannerData(existingPlanner);
          }
          // No planner yet, but one is pending - show pending state and poll
          console.log('🗺️ Planner is being created for objective:', pendingObjective);
          setIsPlannerPending(true);
          setPendingPlannerObjective(pendingObjective);
          // Auto-switch to activities view so user sees the loading card
          switchToActivitiesView();

          // Calculate remaining time before timeout
          const startTime = pendingStartTime ? parseInt(pendingStartTime, 10) : Date.now();
          const remainingTime = PENDING_TIMEOUT_MS - (Date.now() - startTime);

          // Set timeout to clear pending state after remaining time (max 1 hour)
          pendingTimeout = setTimeout(() => {
            console.log('🗺️ Pending planner timed out, clearing state');
            clearPendingState();
            if (pollInterval) {
              clearInterval(pollInterval);
            }
            toast(t('plannerCreationTimeout', 'Learning plan creation timed out. Please try again.'));
          }, Math.max(remainingTime, 0));

          // Start polling every 10 seconds
          pollInterval = setInterval(async () => {
            console.log('🗺️ Polling for pending planner...');
            try {
              const planner = await fetchExistingPlanner();
              const isNewPlan = planner && planner.number_activities > 0
                && (!previousSignature || plannerSignature(planner) !== previousSignature);
              if (isNewPlan) {
                console.log('🗺️ Planner is ready!');
                setCurrentPlannerData(planner);
                clearPendingState();
                setHasPlannerNotification(true);
                clearInterval(pollInterval);
                if (pendingTimeout) {
                  clearTimeout(pendingTimeout);
                }
              }
            } catch (err) {
              console.log('🗺️ Still waiting for planner...');
            }
          }, 10000); // Poll every 10 seconds
        }
      } catch (error) {
        console.log('No existing planner found or error checking:', error);
      } finally {
        setIsPlannerLoading(false);
      }
    };

    // Skip if on /objective — ObjectiveView handles its own polling there
    if (window.location.pathname === '/objective') {
      setIsPlannerLoading(false);
      return;
    }

    // Only check once when component mounts
    checkPlannerNotification();

    // Cleanup polling and timeout on unmount
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
      }
    };
  }, []); // Empty dependency array for one-time check

  // Quietly regenerate the learning plan at the corrected level after a placement test.
  // A placement test writes the new level to Firestore and raises the timestamp flag below;
  // because POST /planner is keyed by level, re-submitting the same objective regenerates a
  // fresh plan at the new level (new plannerId) and clears the old activity-prompt caches.
  // Runs in the background so it doesn't collide with the placement results modal.
  const replanAfterPlacementTest = async (shouldAbort = () => false) => {
    try {
      // Only re-level users who already have a plan; onboarding creates one at the right level otherwise.
      const existingPlan = currentPlannerData || (await fetchExistingPlanner());
      if (!existingPlan?.goal) return;

      // Require a real level. Without one, skip rather than regenerate at a default level,
      // which could downgrade an otherwise-appropriate plan.
      const level = getLatestLevel();
      if (level == null) {
        console.warn('Skipping post-placement re-level: no user level available.');
        return;
      }

      const previousSignature = plannerSignature(existingPlan);
      const storedWork = getStoredWorkInfo();
      const cachedWork = getUserWork() || {};

      const requestData = buildReplanRequest({
        objective: existingPlan.goal,
        level,
        name: getCurrentUserName(),
        age: sharedState.userAge,
        language: sharedState.userLanguage,
        targetLanguage: sharedState.targetLanguage,
        userId: getCurrentUserId(),
        memory: getUserMemory() ? JSON.stringify(getUserMemory()) : undefined,
        work_title: storedWork.work_title || cachedWork.work_title,
        work_domain: storedWork.work_domain || cachedWork.work_domain,
        organizationId: sharedState.organizationId,
        firestoreDocId: getFirestoreDocId(),
      });

      toast(t('plannerRelevelingAfterTest', 'Updating your learning plan to match your new level...'));

      const applyNewPlan = (planning) => {
        setCurrentPlannerData(planning);
        // Mark as viewed so the refreshed plan doesn't raise a spurious "new plan" badge.
        const viewedPlanners = JSON.parse(localStorage.getItem('viewedPlanners') || '[]');
        const plannerKey = `${getCurrentUserId()}_${planning.goal}`;
        if (!viewedPlanners.includes(plannerKey)) {
          viewedPlanners.push(plannerKey);
          localStorage.setItem('viewedPlanners', JSON.stringify(viewedPlanners));
        }
      };

      const response = await axios.post(URL_PLANNER, requestData);
      if (shouldAbort()) return;

      // Fast path: the regenerated plan (or an unchanged cached plan if the level didn't move) comes back directly.
      if (response.data?.planning?.number_activities > 0) {
        applyNewPlan(response.data.planning);
        return;
      }

      // Async path: poll until a genuinely new plan (different first activity) lands, so we
      // never re-apply the still-present old plan during the regeneration window.
      if (response.data && response.data.status === 'processing') {
        let attempts = 0;
        const MAX_ATTEMPTS = 12; // ~3 minutes at 15s intervals
        const poll = async () => {
          if (shouldAbort()) return;
          attempts += 1;
          try {
            const latest = await fetchExistingPlanner();
            if (!shouldAbort() && latest && latest.number_activities > 0 && plannerSignature(latest) !== previousSignature) {
              applyNewPlan(latest);
              return;
            }
          } catch (pollError) {
            // Keep polling within the attempt budget.
          }
          if (!shouldAbort() && attempts < MAX_ATTEMPTS) {
            activePollRef.current = setTimeout(poll, 15000);
          }
        };
        if (activePollRef.current) clearTimeout(activePollRef.current);
        activePollRef.current = setTimeout(poll, 15000);
      }
    } catch (error) {
      // Graceful degradation: leave the existing plan in place if regeneration fails.
      console.warn('Planner re-level after placement test failed:', error?.message);
      Sentry.captureException(error, { tags: { feature: 'planner', action: 'replanAfterPlacementTest' } });
    }
  };

  // Fire the re-level once per placement test (endConversation raises a fresh timestamp each test).
  useEffect(() => {
    if (!sharedState.plannerReplanRequestedAt) return;
    let aborted = false;
    replanAfterPlacementTest(() => aborted);
    return () => {
      // Stop the background poll if the provider unmounts or another placement test fires.
      aborted = true;
      if (activePollRef.current) {
        clearTimeout(activePollRef.current);
        activePollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedState.plannerReplanRequestedAt]);

  const handlePlannerClick = async () => {
    if (isPlannerLoading) return; // Prevent clicks while loading

    // Check if a planner is being created from onboarding
    if (isPlannerPending) {
      toast(t('plannerBeingCreated', 'Your learning plan is being created. Please wait a moment...'));
      return;
    }

    // Check if we already have planner data cached
    if (currentPlannerData && currentPlannerData.number_activities > 0) {
      // Use cached data - no API call needed!
      switchToPlannerView();

      // Mark planner as viewed and remove notification
      const viewedPlanners = JSON.parse(localStorage.getItem('viewedPlanners') || '[]');
      const plannerKey = `${getCurrentUserId()}_${currentPlannerData.goal}`;
      if (!viewedPlanners.includes(plannerKey)) {
        viewedPlanners.push(plannerKey);
        localStorage.setItem('viewedPlanners', JSON.stringify(viewedPlanners));
        setHasPlannerNotification(false);
      }
      return;
    }

    // No cached data - fetch from backend
    try {
      const existingPlanner = await fetchExistingPlanner();
      if (existingPlanner && existingPlanner.number_activities > 0) {
        // User has existing planner, switch to planner view
        setCurrentPlannerData(existingPlanner);
        switchToPlannerView();

        // Mark planner as viewed and remove notification
        const viewedPlanners = JSON.parse(localStorage.getItem('viewedPlanners') || '[]');
        const plannerKey = `${getCurrentUserId()}_${existingPlanner.goal}`;
        if (!viewedPlanners.includes(plannerKey)) {
          viewedPlanners.push(plannerKey);
          localStorage.setItem('viewedPlanners', JSON.stringify(viewedPlanners));
          setHasPlannerNotification(false);
        }
      } else {
        // No existing planner, show modal to create new one
         setShowPlannerModal(true);
      }
    } catch (error) {
      console.error('Error fetching planner:', error);
      // Fallback to creating new planner
      setShowPlannerModal(true);
    }
  };

  // Common function to handle successful planner creation
  const handlePlannerSuccess = (planningData, isFromPolling = false) => {
    const successMessage = isFromPolling
      ? 'Your learning plan is ready! You can view it by clicking "View Learning Plan".'
      : 'Thank you for your objective. Your activity planner has been created! You can view it below.';

    toast(successMessage);

    // Track goal creation or editing
    const isEditing = plannerGoalForEdit && plannerGoalForEdit.trim() !== '';
    if (isEditing) {
      trackLearningGoalEdited(
        plannerGoalForEdit,
        planningData.goal,
        getLatestLevel() ?? 3, // Default to 3 if user has no level set
        sharedState.userAge || '20',
        sharedState.userLanguage || 'en'
      );
    } else {
      trackLearningGoalCreated(
        planningData.goal,
        getLatestLevel() ?? 3, // Default to 3 if user has no level set
        sharedState.userAge || '20',
        sharedState.userLanguage || 'en'
      );
    }

    // Track planner creation (this tracks the complete planner with activities)
    trackPlannerCreated(
      planningData.goal,
      planningData.number_activities,
      getLatestLevel() ?? 3, // Default to 3 if user has no level set
      sharedState.userAge || '20',
      sharedState.userLanguage || 'en'
    );

    // Set the new planner data but STAY on activities view
    setCurrentPlannerData(planningData);
    setShowPlannerModal(false);
    setPlannerGoalForEdit(''); // Reset the edit goal state
    // switchToPlannerView(); // REMOVED - user stays on activities view
    setIsPlannerLoading(false); // Ensure loading is stopped

    // Mark new planner as viewed (no notification needed)
    const viewedPlanners = JSON.parse(localStorage.getItem('viewedPlanners') || '[]');
    const plannerKey = `${getCurrentUserId()}_${planningData.goal}`;
    if (!viewedPlanners.includes(plannerKey)) {
      viewedPlanners.push(plannerKey);
      localStorage.setItem('viewedPlanners', JSON.stringify(viewedPlanners));
    }
  };

  const handlePlannerSubmit = async (plannerText) => {
    if (!plannerText.trim()) {
      toast('Please enter some objective before submitting.');
      return;
    }

    // Check if user has existing planner data and show confirmation
    if (currentPlannerData && currentPlannerData.number_activities > 0) {
      if (!window.confirm(getReplacePlanConfirmMessage(currentPlannerData, t))) {
        return; // User cancelled, don't proceed
      }
    }

    // Prepare the data to be sent in the request.
    // Prefer work info cached during onboarding (localStorage) over the auth-time
    // cache, which is empty for a brand-new user creating their first plan.
    const storedWork = getStoredWorkInfo();
    const cachedWork = getUserWork() || {};
    const work_title = storedWork.work_title || cachedWork.work_title || '';
    const work_domain = storedWork.work_domain || cachedWork.work_domain || '';
    const requestData = {
      firestoreDocId: getFirestoreDocId(),
      name: getCurrentUserName(),
      level: getLatestLevel() ?? 3, // Default to 3 if user has no level set
      age: sharedState.userAge || '20',
      language: sharedState.userLanguage || 'English',
      userId: getCurrentUserId(),
      activity: sharedState.selectedActivity || localStorage.getItem('selectedActivity'),
      memory: getUserMemory() || undefined,
      work_title: work_title || undefined,
      work_domain: work_domain || undefined,
      targetLanguage: sharedState.targetLanguage || 'French', // Language being learned
      organizationId: sharedState.organizationId || undefined, // Organization ID from URL param
      objective: plannerText
    };

    // Cancel any active polling from a previous submission
    if (activePollRef.current) {
      clearTimeout(activePollRef.current);
      activePollRef.current = null;
    }

    setIsPlannerLoading(true); // Start loading

    // When replacing an existing plan, the latest planner stays the OLD one
    // until generation finishes — polling must wait for a signature change,
    // not just for any plan to exist.
    const previousSignature = plannerSignature(currentPlannerData);

    try {
      const response = await axios.post(URL_PLANNER, requestData);

      if (response.data && response.data.status === 'processing') {
        // Handle async processing case
        toast('Your learning plan is being generated! Feel free to practice with a Freeform conversation in the meantime. You\'ll be notified when it\'s ready (up to 90 seconds).');
        setShowPlannerModal(false); // Close modal but keep loading
        switchToActivitiesView(); // Navigate back to activities during generation

        // Start polling for completion with max retries to prevent infinite loops
        const MAX_POLL_ATTEMPTS = 12; // ~3 minutes at 15s intervals
        let pollAttempt = 0;

        const pollForCompletion = async () => {
          pollAttempt++;
          try {
            const existingPlanner = await fetchExistingPlanner();
            const hasPlan = existingPlanner && existingPlanner.number_activities > 0;
            const isNewPlan = hasPlan && plannerSignature(existingPlanner) !== previousSignature;

            if (isNewPlan) {
              // Plan is ready! Use common success handler
              handlePlannerSuccess(existingPlanner, true);
            } else if (pollAttempt >= MAX_POLL_ATTEMPTS) {
              if (hasPlan) {
                // The regenerated plan can share the old signature (same goal
                // and first activity) — settle on the latest rather than error.
                handlePlannerSuccess(existingPlanner, true);
              } else {
                // Give up after max attempts
                console.error(`🗺️ Planner polling timed out after ${pollAttempt} attempts`);
                setIsPlannerLoading(false);
                clearPendingState();
                toast(t('plannerCreationTimeout', 'Learning plan creation timed out. Please try again.'));
              }
            } else {
              // Still processing (or only the old plan exists), poll again
              activePollRef.current = setTimeout(pollForCompletion, response.data.pollInterval || 15000);
            }
          } catch (error) {
            console.error('Error polling for planner completion:', error);
            setIsPlannerLoading(false);
            clearPendingState();
            activePollRef.current = null;
            toast('There was an issue checking your learning plan. Please try refreshing the page.');
          }
        };

        // Start polling after the specified interval
        activePollRef.current = setTimeout(pollForCompletion, response.data.pollInterval || 15000);

      } else if (response.data && response.data.planning) {
        // Immediate success - navigate to activities and show planner data
        setShowPlannerModal(false);
        switchToActivitiesView(); // Navigate to activities view
        handlePlannerSuccess(response.data.planning, false); // Show success, stay on activities
      } else {
        toast('Could not create planner at this time.'); // Error planner
        setIsPlannerLoading(false);
      }
    } catch (error) {
      console.error('Error creating planner:', error);
      toast('Could not submit objective at this time.'); // Error planner
      setIsPlannerLoading(false);
    }
  };

  // Handle goal editing from PlannerActivityList
  const handlePlannerGoalEdit = (currentGoal) => {
    setPlannerGoalForEdit(currentGoal);
    setShowPlannerList(false);
    setShowPlannerModal(true);
  };

  // Handle activity selection from PlannerActivityList
  const handlePlannerActivitySelect = async (activityValue) => {
    setShowPlannerList(false);
    updateSelectedActivity(activityValue, true);
    // Switch to conversation view and start the conversation properly
    switchToConversationView();
    // Use the proper conversation start handler instead of setting states directly
    // await startConversation(activityValue);
    	// Delay the call to startConversation to allow the handler to register
    setTimeout(() => {
      console.log('[PlannerContext] Calling startConversation with', activityValue)
      startConversation(activityValue);
    }, 50); // 0ms is usually enough, but you can increase to 50-100ms if needed
  };

  // Handle going back from PlannerActivityList to activities
  const handlePlannerGoBack = () => {
    setShowPlannerList(false);
    setCurrentPlannerData(null);
    // switchToActivities() is handled by the component that uses this context
  };

  // Handle modal close with proper state management
  const handlePlannerModalClose = () => {
    setShowPlannerModal(false);
    setPlannerGoalForEdit(''); // Reset pre-filled value
    // if (currentPlannerData) {
    //   // If user had planner data but cancelled editing, show the list again
    //   setShowPlannerList(true);
    // }
  };

  // Check if user has planner data, if not, show goal prompt modal
  const checkPlannerDataOrPromptGoal = async () => {
    // If planner is being created from onboarding, don't prompt again
    if (isPlannerPending) {
      toast(t('plannerBeingCreated', 'Your learning plan is being created. Please wait a moment...'));
      return true; // Return true to prevent prompting
    }

    try {
      const existingPlanner = await fetchExistingPlanner();
      if (!existingPlanner || existingPlanner.number_activities === 0) {
         // No planner data, show goal prompt
         setShowPlannerModal(true);
        return false; // No planner data
      }
      return true; // Has planner data
    } catch (error) {
      console.log('Error checking planner data:', error);
      // On error, show goal prompt as fallback
      setShowPlannerModal(true);
      return false;
    }
  };

  return (
    <PlannerContext.Provider value={{
      // State
      showPlannerModal,
      showPlannerList,
      currentPlannerData,
      plannerGoalForEdit,
      isPlannerLoading,
      isPlannerPending,
      pendingPlannerObjective,
      hasPlannerNotification,

      // Actions
      handlePlannerClick,
      handlePlannerSubmit,
      handlePlannerGoalEdit,
      handlePlannerActivitySelect,
      handlePlannerGoBack,
      handlePlannerModalClose,
      checkPlannerDataOrPromptGoal,
      switchToPlannerView,
      
      // State setters for advanced use cases
      setShowPlannerModal,
      setShowPlannerList,
      setCurrentPlannerData
    }}>
      {children}
    </PlannerContext.Provider>
  );
};
